import React from 'react';
import { Recipe } from '../../types/api/entities/recipe';
import { mediaQuery } from '../../util/ui';
import { MediaBar } from './media';
import { RecipeSummary } from './summary';
import { SignupSource, Store } from '../../types/api/entities/store';
import { StoreState } from '../../store/state';
import { connect } from 'react-redux';
import * as QRCode from 'qrcode';
import { LinkCreator } from '../../util/linker';
import { formatTitle } from '../../util/format-title';
import { withRecipeJsonLd } from './with-recipe-json-ld';
import { RecipeFilter } from '../../types/api/entities/recipe-filter';
import Head from 'next/head';
import { BackLink } from '../back-link';
import { withTranslation } from 'next-i18next';

import { WithTranslation as WithTranslationType } from 'next-i18next';
import 'react-responsive-carousel/lib/styles/carousel.css';
import { MediaCarousel } from './carousel';
import { RecipeProducts } from './products';
import { LinkedRecipeCard } from './card';

export interface OwnProps {
  recipe: Recipe;
}

export interface StateProps {
  store: Store;
  filters: (RecipeFilter | undefined)[];
}

export type RecipeDetailsProps = OwnProps & StateProps & WithTranslationType;

interface RecipeDetailsState {
  qr: string;
}

const QR_WIDTH = 169;

class RecipeDetailsComponent extends React.Component<RecipeDetailsProps, RecipeDetailsState> {
  constructor(props: RecipeDetailsProps) {
    super(props);
    this.state = {
      qr: '',
    };
  }

  public render() {
    const { recipe, store } = this.props;
    return (
      <>
        <div className="back-link">
          <BackLink text={this.props.t('AllRecipes')} href={LinkCreator.toRoot()} />
        </div>

        <div className="recipe-details">
          <Head>
            <title>
              {formatTitle(store, this.props.t('RecipeName', { recipeName: recipe.name }))}
            </title>
          </Head>

          <div className="header">
            <div className="title-container">
              <div className="logo" />
              <h1 className="title">{recipe.name}</h1>
            </div>

            <div className="qr">
              <img src={this.state.qr} alt="qr" width="100%" height="100%" />
            </div>

            <div className="media">
              <MediaBar
                onPrintClick={this.onPrint}
                recipe={recipe}
                url={LinkCreator.absoluteRecipeLink(store, recipe)}
              />
            </div>

            <div className="image-container">
              <MediaCarousel recipe={recipe} />
            </div>
          </div>

          <div className="summary">
            <RecipeSummary
              deactivateRatings={store.theme?.deactivateRatings}
              deactivateAuthor={store.theme?.deactivateAuthor}
              recipe={recipe}
            />
          </div>

          <div className="content">
            <div className="description" dangerouslySetInnerHTML={{ __html: recipe.description }} />

            <div>
              <h2 className="content__title">{this.props.t('Ingredients')}</h2>

              <div className="ingredients">
                <ul>
                  {recipe.ingredients.map((item, index) => (
                    <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                  ))}
                </ul>

                {store.signupSource === SignupSource.Shopify && (
                  <div className="products">
                    <RecipeProducts recipeId={recipe.id} />
                  </div>
                )}
              </div>
            </div>

            <h2 className="content__title">{this.props.t('Instructions')}</h2>

            <ol>
              {recipe.instructions.map((item, index) => (
                <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
              ))}
            </ol>

            {recipe.notes ? (
              <>
                {' '}
                <h2 className="content__title">{this.props.t('Tips')}</h2>
                <div className="notes" dangerouslySetInnerHTML={{ __html: recipe.notes }} />
              </>
            ) : (
              ''
            )}

            <div className="recommended">
              <h3 className="recommended__title">{this.props.t('OtherRecipes')}</h3>
              <div className="recommended__content">
                {recipe.related.map((item) => (
                  <div key={item.id} className="recipe-card">
                    <LinkedRecipeCard recipe={item} showRating={!store.theme?.deactivateRatings} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <style jsx>{`
          @media screen, print {
            .title {
              font-family: var(--font-family-secondary);
              font-weight: normal;
              line-height: normal;
              color: #131415;
              margin: 0;
            }

            .description,
            .notes,
            .content ul,
            .content ol {
              font-family: var(--font-family-primary);
              color: #424242;
            }

            .summary {
              display: flex;
              justify-content: center;
            }

            .description {
              margin: 0;
              white-space: pre-wrap;
            }

            .description :global(p) {
              margin-bottom: 10px;
            }

            .description :global(h2),
            .description :global(h3),
            .description :global(h4),
            .description :global(h5),
            .description :global(h6),
            .content__title {
              margin-top: 15px;
              margin-bottom: 0;
              font-family: var(--font-family-secondary);
              color: #1d1e1f;
              font-weight: bold;
            }

            .description :global(li) {
              list-style: none;
              margin-left: 20px;
            }

            .description :global(li::before) {
              content: '•';
              color: #d3d3d3;
              margin-right: 10px;
            }

            .content ul,
            .content ol {
              margin-bottom: 0;
              padding: 0;
              list-style: none;
            }

            .content li:not(:first-child) {
              margin-top: 10px;
            }

            .content li::before {
              color: #424242;
              display: inline-block;
              width: 23px;
            }

            .content ul li::before {
              /* Using escape code here shows huge error/warning in the console */
              content: '•';
            }

            .content ol li {
              counter-increment: instructions-counter;
            }

            .content ol li::before {
              content: counter(instructions-counter) '.';
            }

            .products {
              margin-top: 12px;
            }
          }

          @media screen {
            .logo,
            .qr {
              display: none;
            }

            .recipe-details {
              margin-top: 5px;
            }

            .title,
            .back-link {
              text-align: center;
            }

            .back-link {
              margin-top: 2px;
            }

            .title {
              font-size: 30px;
            }

            .title,
            .content {
              padding-left: 10px;
              padding-right: 10px;
              // TODO: Fix padding on large size of medium resolution.
            }

            .media {
              margin-top: 10px;
              text-align: center;
            }

            .image-container {
              display: flex;
              flex-direction: column;
              margin-top: 17px;
            }

            .content {
              margin-top: 40px;
              padding-bottom: 64px;
            }

            .description {
              font-size: 16px;
              line-height: 1.5;
            }

            .content__title,
            .description :global(h2) {
              margin-top: 30px;
              font-size: 18px;
              line-height: 1.44;
            }

            .description :global(h3) {
              margin-top: 24px;
              font-size: 14px;
              line-height: 1.3;
            }

            .content ul,
            .content ol {
              margin-top: 12px;
              font-size: 16px;
              line-height: 1.5;
            }

            .recommended {
              margin-top: 30px;
            }

            .recommended__title {
              margin: 0;
              font-family: var(--font-family-secondary);
              font-size: 18px;
              line-height: 1.44;
              color: #1d1e1f;
              font-weight: normal;
            }

            .recommended__content {
              display: flex;
              flex-direction: column;
            }

            .recipe-card {
              width: 100%;
              margin-top: 14px;
            }
          }

          @media ${mediaQuery.Medium} {
            .title,
            .content {
              padding-left: 24px;
              padding-right: 24px;
            }

            .content {
              padding-bottom: 49px;
            }

            .image-container {
              margin-top: 23px;
            }

            .recommended__content {
              flex-direction: row;
            }

            .recipe-card {
              margin-top: 21px;
            }

            .recipe-card:not(:first-child) {
              margin-left: 30px;
            }

            .ingredients {
              display: flex;
              justify-content: space-between;
            }

            .products {
              margin-top: 0;
              width: 100%;
              max-width: 300px;
            }
          }

          @media ${mediaQuery.ExtraLarge} {
            .recipe-details {
              display: grid;
              grid-template-columns: [left] 175px 135px [right] auto;
              margin: 17px 0 0;
            }

            .header,
            .content {
              grid-column-start: right;
            }

            .summary {
              grid-column-start: left;
              grid-row-start: 1;
            }

            .title,
            .back-link {
              text-align: left;
            }

            .back-link {
              margin: 0;
            }

            .title {
              font-size: 60px;
            }

            .title,
            .content {
              padding-left: 0;
              padding-right: 0;
            }

            .content {
              padding-bottom: 40px;
            }

            .media {
              margin-top: 20px;
              text-align: left;
            }

            .image-container {
              margin-top: 37px;
            }

            .description {
              font-size: 18px;
              line-height: 1.67;
            }

            .content__title,
            .description :global(h2) {
              margin-top: 40px;
              font-size: 24px;
              line-height: 1.42;
            }

            .description :global(h3) {
              margin-top: 30px;
              font-size: 20px;
              line-height: 1.32;
            }

            .content ul,
            .content ol {
              margin-top: 14px;
              font-size: 18px;
              line-height: 1.78;
            }

            .content li {
              position: relative;
            }

            .content li::before {
              position: absolute;
              top: 0;
              right: calc(100% + 25px);
              color: #d3d3d3;
              font-family: var(--font-family-secondary);
              font-weight: bold;
              width: auto;
            }

            .content ul li::before {
              font-size: 18px;
              line-height: 1.78;
            }

            .content ol li::before {
              line-height: 1.42;
              font-size: 24px;
            }

            .recommended__title {
              font-size: 24px;
              line-height: 1.42;
            }

            .recipe-card {
              margin-top: 31px;
            }
          }

          @media print {
            .recipe-details {
              padding-bottom: 58px;
            }

            .media,
            .image-container,
            .recommended,
            .back-link {
              display: none;
            }

            .header {
              display: grid;
              grid-template-columns: auto auto;
              grid-template-rows: auto;
            }

            .title-container {
              display: flex;
              flex-direction: column;
              justify-content: flex-end;
            }

            .logo {
              width: 83px;
              height: 38px;
              background-image: url(${store.logoUrl});
              background-size: contain;
              background-repeat: no-repeat;
              background-position: left bottom;
            }

            .title {
              margin-top: 25px;
              font-size: 42px;
            }

            .qr {
              width: ${QR_WIDTH}px;
              height: ${QR_WIDTH}px;
              border: solid 2px #131415;
              grid-column-start: 2;
              justify-self: end;
            }

            .summary {
              margin-top: 22px;
            }

            .content {
              margin-top: 28px;
            }

            .content__title {
              font-size: 24px;
              line-height: 1.42;
            }

            .content__title:nth-of-type(1) {
              margin-top: 24px;
            }

            .content__title:not(:nth-of-type(1)) {
              margin-top: 35px;
            }

            .description,
            .content li {
              font-size: 18px;
            }

            .description {
              line-height: 1.67;
            }

            .content li {
              line-height: 1.78;
            }
          }
        `}</style>
      </>
    );
  }

  private readonly onPrint = async () => {
    if (this.state.qr) {
      window.print();
    } else {
      this.setState(
        {
          qr: await QRCode.toDataURL(window.location.href, {
            width: QR_WIDTH,
          }),
        },
        () => {
          setTimeout(() => {
            window.print();
          }, 0);
        },
      );
    }
  };
}

function mapStateToProps(store: StoreState, props: OwnProps): StateProps {
  const {
    filter: { byId: filtersById, idByOptionId: filterIdsByOptionId },
  } = store.entities;

  const result: StateProps = {
    store: store.entities.store,
    filters: props.recipe.filters.map((optionId) => {
      const optionFilters = filterIdsByOptionId[optionId];
      if (optionFilters) {
        return filtersById[optionFilters];
      }

      return undefined;
    }),
  };

  return result;
}

export const RecipeDetails = withTranslation()(
  connect(mapStateToProps)(withRecipeJsonLd(RecipeDetailsComponent)),
);
